import React from 'react'
import { useLocation } from 'react-router-dom'
import { t } from 'i18next'
import { useRootDispatch } from '@app/store'

import {
  Flex,
  NavItem,
  Spacing,
  Text,
  Icon,
  Link,
  SubscriptionSeats
} from '@ui'
import { useRoutingConfig } from '@app/routing'
import {
  debugUtil,
  miscUtil,
  routeUtil,
  calendarUtil,
  isValue
} from '@app/util'
import { Logo, LogoWhite } from '@app/image'
import connect from './connect'
import {
  Help,
  Me,
  NavPanel,
  WsSelection,
  PaywallItem,
  NotificationItem
} from './components'
import { useChatUnread } from '@app/util/hooks/chat/useChatUnread'
import { useCanUpdateTiers } from '@app/util/hooks/tiers-hooks'
import { useModal } from '@app/util/modal/use-modal'
import { EmployeeCardV2 } from '@app/component/modals/employee-card-v2/employee-card-v2'
import { SetFeeds } from '@app/ac/set-news-feed'

export const Navigation = connect(({
  workspaces,
  workspace,
  workspaceId,
  me,
  auth,
  isPluginEnabled,
  isLoading,
  logout,
  setModal,
  setSidebar,
  subscription,
  organizationId,
  employees,
  positions,
  templates,
  newsFeed,
  isOrgOwner
}) => {
  const dispatch = useRootDispatch()
  const routes = useRoutingConfig({
    auth,
    isPluginEnabled,
    setModal,
    workspaceId,
    workspace,
    me,
    subscription,
    isOrgOwner
  })
  const location = useLocation()
  const isOrg = miscUtil.isDayswapsProInterface()
  const isPaidOrTrial =
    subscription?.subscriptionStatus === 'paid' || subscription?.subscriptionStatus === 'trial'
  const currentRoutes = routes[isOrg ? 'pro' : 'app']
  const currentPath = currentRoutes.find((route) => location.pathname.includes(route.path))
  const wsName = (isOrg && organizationId) ? me?.organizations && me.organizations.find(org => org.id === organizationId)?.name : (workspace?.name)
  const canUpdateTiers = useCanUpdateTiers()

  const { openModal } = useModal()

  const chatUnread = useChatUnread()

  const newsFeedUnread = isValue(newsFeed) && Object.values(newsFeed).length > 0
    ? newsFeed.map((item) => item.targets.map((ns) => ns.userId === me.id && !ns.readOn))
      .flat(1)
      .filter(Boolean).length : 0
  // const userAttributesCount = useRootSelector((state) =>
  //   state.userAttributes.userAttributes.filter(Boolean).map((ua) => ({
  //     ...ua,
  //     values: ua.options?.values?.join(',') ?? ''
  //   }))
  // ).length

  const userAttributesCount = workspace?.userAttributes?.length ?? 0

  const showWsSelection =
    Boolean(workspaces?.length) || Boolean(Object.keys(me?.acl?.organizations || {}).length)

  React.useEffect(() => {
    const action = async () => {
      if (Object.keys(newsFeed).length === 0) {
        dispatch(SetFeeds({ auth, userId: me.id }))
      }
    }

    action()
  }, [])

  const numbers = [
    {
      path: '/news-feed',
      number: newsFeedUnread
    },
    {
      path: '/chat',
      number: chatUnread
    },
    {
      path: '/employees',
      number: employees ? Object.keys(employees).map(id => employees[id]).filter(ee => !ee.external).length : undefined
    },
    {
      path: '/job-positions',
      number: positions ? positions.filter(p => !p.archived).length : undefined
    },
    {
      path: '/localities',
      number: workspace?.localities ? workspace.localities.length : undefined
    },
    {
      path: '/timeoff-categories',
      number: workspace?.unavailabilityCategories ? workspace.unavailabilityCategories.length : undefined
    },
    {
      path: '/shift-templates',
      number: workspace?.shiftTemplates ? workspace.shiftTemplates.length : undefined
    },
    {
      path: '/day-templates',
      number: templates ? templates.length : undefined
    },
    {
      path: '/cycles',
      number: workspace?.cycles ? workspace.cycles.length : undefined
    },
    // {
    //   path: '/attributes',
    //   number: workspace?.shiftAttributes ? calendarUtil.getSupportedShiftAttributes(workspace).length : undefined
    // },
    {
      path: '/shift-attributes',
      number: workspace?.shiftAttributes ? calendarUtil.getSupportedShiftAttributes(workspace).length : undefined
    },
    {
      path: '/groups',
      number: window.localStorage.getItem('ds-added-groups') ? window.localStorage.getItem('ds-added-groups')?.split(',')?.length : undefined
    },
    {
      path: '/custom-attributes',
      number: userAttributesCount
    }
  ]
  const findNumber = (path) => {
    const item = numbers.find((number) => number.path === path)
    return typeof item?.number === 'number' ? item.number : null
  }

  const linkItem = (item) => {
    const allowedSubPages = item.subpages && item.subpages.filter((item) => item.allowed)
    const firstPath = allowedSubPages && item.path + allowedSubPages[0]?.path
    const decideLink = () => {
      if (item.link === 'DECIDE') {
        return isOrg ? '/organization' + firstPath : firstPath
      }
      if (item.link) {
        return item.link
      }
      if (allowedSubPages) {
        return isOrg ? '/organization' + firstPath : firstPath
      }
      return isOrg ? '/organization' + item.path : item.path
    }

    const isChat = item.ico === 'chat'

    return (
      <Link
        key={item.path}
        to={decideLink()}
        type={Link.TYPES.ROUTER_LINK}
      >
        <NavItem
          dark={isOrg}
          ico={item.ico}
          text={t(item.label)}
          isActive={location.pathname.includes(item.path)}
          number={isChat && (chatUnread + newsFeedUnread) > 0 ? (chatUnread + newsFeedUnread) : 0}
        />
      </Link>
    )
  }
  const handleDebug = debugUtil.info

  return (
    <NavPanel
      dark={isOrg}
      main={
        <>
          <div>
            <Spacing size={Spacing.SIZES.SIZE_18} />
            <Flex justify={Flex.POSITION.CENTER} grow={1}>
              <Link
                type={Link.TYPES.ON_CLICK}
                onClick={(evt) => {
                  if (evt.ctrlKey || evt.metaKey) {
                    handleDebug()
                    evt.preventDefault()
                    evt.stopPropagation()
                  } else {
                    routeUtil.navigate('/')
                  }
                }}
              >
                {isOrg ? <LogoWhite width='2.5rem' height='3rem' /> : <Logo width='2.5rem' />}
              </Link>
            </Flex>
            <Spacing size={Spacing.SIZES.SIZE_10} />
            {showWsSelection && (
              <WsSelection
                onClick={() => {
                  setSidebar(null)
                  setModal('workspace-select')
                }}
                isOrg={isOrg}
                wsName={wsName}
              />
            )}
            <Spacing size={Spacing.SIZES.SIZE_8} />
            <Flex
              direction={Flex.DIRECTION.COLUMN}
              align={Flex.POSITION.CENTER}
              justify={Flex.POSITION.SPC_BETWEEN}
            >
              <Flex
                direction={Flex.DIRECTION.COLUMN}
                align={Flex.POSITION.CENTER}
              >
                {currentRoutes
                  .filter((item) => item.allowed && item.label && !item.atBottom).filter(item => !item.hidden)
                  .map((item) => linkItem(item))}
              </Flex>

            </Flex>
          </div>
          <div>
            {/* Display everything except settings tab */}
            <Flex
              direction={Flex.DIRECTION.COLUMN}
              align={Flex.POSITION.CENTER}
            >
              {currentRoutes
                .filter(item => item.path !== '/settings')
                .filter((item) => item.allowed && item.label && item.atBottom)
                .map((item) => linkItem(item))}
            </Flex>
            <Spacing size={Spacing.SIZES.SIZE_12} />
            {isPaidOrTrial && !isOrg && (<NotificationItem />)}
            {/* Display only settings tab */}
            <Flex
              direction={Flex.DIRECTION.COLUMN}
              align={Flex.POSITION.CENTER}
            >
              {currentRoutes
                .filter(item => item.path === '/settings')
                .filter((item) => item.allowed && item.label && item.atBottom)
                .map((item) => linkItem(item))}
            </Flex>

            {
              canUpdateTiers ? (
                <Link
                  to={isOrg ? '/organization/invoicing' : '/invoicing'}
                  type={Link.TYPES.ROUTER_LINK}
                >
                  <NavItem
                    dark={isOrg}
                    ico={Icon.ICONS.payroll}
                    text={t('SUBSCRIPTION')}
                    isActive={location.pathname.includes('/invoicing')}
                  />
                </Link>
              ) : false
            }

            {canUpdateTiers ? (
              <PaywallItem
                subscription={subscription}
                isLoading={isLoading}
              />
            ) : false}

            <Help
              className='is-navigation-help'
              anchor={(
                <NavItem
                  ico='help'
                  text={t('SCHEDULE_HELP')}
                  dark={isOrg}
                />
              )}
            />
            <Spacing size={Spacing.SIZES.SIZE_28} />
            <Flex justify={Flex.POSITION.CENTER} grow={1}>
              <Me
                isLoading={isLoading}
                me={me}
                isOrg={isOrg}
                logOut={logout}
                // openEmployeeCard={() => setModal('employee-profile', { userId: me.id })}
                openEmployeeCard={() => openModal(EmployeeCardV2, { userId: me.id })}
                isPluginEnabled={isPluginEnabled}
              />
            </Flex>
            <Spacing size={Spacing.SIZES.SIZE_20} />
          </div>
        </>
      }
      sub={!!currentPath?.subpages && !!currentPath.subpages.length && (
        <div
          style={{
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            height: '100%'
          }}
        >
          <Flex direction={Flex.DIRECTION.COLUMN}>
            <Spacing size={Spacing.SIZES.SIZE_28}>
              <Spacing size={Spacing.SIZES.SIZE_16} type={Spacing.TYPES.HORIZONTAL}>
                <Text
                  type={Text.TYPES.HEADLINE2}
                  text={t(currentPath.label)}
                  color={isOrg ? Text.COLORS.WHITE : Text.COLORS.PRIMARY}
                />
              </Spacing>
            </Spacing>
            <Spacing size={Spacing.SIZES.SIZE_4} type={Spacing.TYPES.HORIZONTAL}>
              {currentPath?.subpages && currentPath.subpages
                .filter((item) => item.allowed)
                .map((item, i) => {
                  if (!item.label) return null
                  const subItemPath = currentPath.path + item.path
                  return (
                    <React.Fragment key={item.path}>
                      {item.groupTitle && (
                        <>
                          {i !== 0 && (<Spacing size={Spacing.SIZES.SIZE_18} />)}
                          <Spacing size={Spacing.SIZES.SIZE_12} type={Spacing.TYPES.HORIZONTAL}>
                            <Text
                              type={Text.TYPES.BODY_LARGE}
                              weight={Text.WEIGHTS.BOLD}
                              color={isOrg ? Text.COLORS.GRAY : Text.COLORS.BLACK}
                              text={t(item.groupTitle)}
                            />
                          </Spacing>
                          <Spacing size={Spacing.SIZES.SIZE_8} />
                        </>
                      )}
                      <Link
                        onClick={item.onClick || undefined}
                        type={item.onClick ? Link.TYPES.ON_CLICK : Link.TYPES.ROUTER_LINK}
                        to={item.onClick ? undefined : (item.disabled ? '#' : (item.link || isOrg ? '/organization' + subItemPath : subItemPath))}
                      >
                        <NavItem
                          dark={isOrg}
                          ico={item.ico}
                          type={NavItem.TYPES.SUB}
                          text={t(item.label)}
                          isActive={location && location.pathname.includes(item.path)}
                          number={findNumber(item.path)}
                          disabled={item.disabled}
                        />
                      </Link>
                    </React.Fragment>
                  )
                })}
            </Spacing>
          </Flex>
          {(isOrg || location?.pathname === '/workspace/employees')
            ? (
              <div>
                <Flex>
                  <Spacing size={Spacing.SIZES.SIZE_16} type={Spacing.TYPES.HORIZONTAL} /> <SubscriptionSeats isOrg={isOrg} />
                </Flex>
                <Spacing size={Spacing.SIZES.SIZE_20} type={Spacing.TYPES.VERTICAL} />
              </div>
            )
            : false}
        </div>
      )}
    />
  )
})
